import React, { useContext, useState, useRef } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import { Swipeable } from 'react-swipeable';
import { SafeComponent, LazyComponent } from '@fiverr-private/orca';
import { getContext } from '@fiverr-private/fiverr_context';
import { logger } from '@fiverr-private/obs';
import Slideshow from '@fiverr-private/slideshow/src/Slideshow';
import AppContext from '../../state/AppContext';
import { reportAssetClicked } from '../../utils/report/bigquery';
import { KEY_CODE, SLIDE_TYPE } from './constants';
import { shouldPreloadSlide } from './utils';
import SlideWrapper from './utils/SlideWrapper';

import style from './style.module.scss';
import './style.scss';

const lazyImportGalleryModal = () => import(/* webpackChunkName: 'GalleryModal' */ './GalleryModal');

const onLazyImportGalleryModalError = (error) => {
    logger.error('Failed to lazy load shared assets gallery modal', { error });
};

const Gallery = ({ transition = 'slide-h' }) => {
    const { isTouch } = getContext();
    const storeContext = useContext(AppContext);
    const {
        assetsSharing: { assets: assetsGallery },
    } = storeContext;
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [shouldImportGalleryModal, setShouldImportGalleryModal] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const wrapperRef = useRef();

    const changeSlide = (index = 0) => {
        setCurrentSlideIndex(index >= 0 ? index % assetsGallery.length : assetsGallery.length - 1);
        wrapperRef.current?.focus({ preventScroll: true });
    };

    const handlePrev = (e) => {
        e?.preventDefault && e.preventDefault();
        changeSlide(currentSlideIndex - 1);
    };

    const handleNext = (e) => {
        e?.preventDefault && e.preventDefault();
        changeSlide(currentSlideIndex + 1);
    };

    const handleKeyDown = (e) => {
        if (e) {
            switch (e.keyCode) {
                case KEY_CODE.ARROW_LEFT: {
                    handlePrev(e);
                    break;
                }
                case KEY_CODE.ARROW_RIGHT: {
                    handleNext(e);
                    break;
                }
                default: {
                    break;
                }
            }
        }
    };

    const handleSlideClick = () => {
        const { type } = assetsGallery[currentSlideIndex];

        if (type === SLIDE_TYPE.IMAGE || type === SLIDE_TYPE.PDF) {
            if (!shouldImportGalleryModal) {
                setShouldImportGalleryModal(true);
            }
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = (index) => {
        changeSlide(index);
        setIsModalOpen(false);
    };

    const generateSlide = (slide, index) => {
        const key = `${index}--${slide.media.medium || slide.src}--gallery`;
        const { resourceId, type } = slide;

        const slideProps = {
            slide,
            key,
            isActive: index === currentSlideIndex,
            expanded: false,
            index,
            preload: shouldPreloadSlide(index, currentSlideIndex),
            reportAction: (params = {}) => {
                reportAssetClicked(storeContext, {
                    resourceId,
                    position: index + 1,
                    ...params,
                });
            },
        };

        return <SlideWrapper type={type} slideProps={slideProps} key={`wrapper--${key}`} />;
    };

    const galleryModalProps = {
        slides: assetsGallery,
        selectedIndex: currentSlideIndex,
        transition: 'fade-slide-h',
        onClose: handleCloseModal,
        isOpen: isModalOpen,
    };

    return (
        <section
            className={classNames('assets-sharing-perseus-gallery-wrapper', style.galleryWrapper, {
                [style.touch]: isTouch,
            })}
            ref={wrapperRef}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            aria-label="gallery"
        >
            <div className={style.gallerySlideshow}>
                {assetsGallery.length > 1 && (
                    <>
                        <a key="ss-prev" href="#" className={style.navPrev} onClick={handlePrev} rel="nofollow" />
                        <a key="ss-next" href="#" className={style.navNext} onClick={handleNext} rel="nofollow" />
                    </>
                )}
                <Swipeable
                    onSwipedLeft={handleNext}
                    onSwipedRight={handlePrev}
                    preventDefaultTouchmoveEvent
                    className={style.swipeEventsWrapper}
                >
                    <Slideshow selectedIndex={currentSlideIndex} transition={transition} onClick={handleSlideClick}>
                        {assetsGallery.map(generateSlide)}
                    </Slideshow>
                </Swipeable>
            </div>
            <LazyComponent
                lazyImport={lazyImportGalleryModal}
                shouldImport={shouldImportGalleryModal}
                onError={onLazyImportGalleryModalError}
                componentProps={galleryModalProps}
            />
        </section>
    );
};

Gallery.propTypes = {
    transition: string,
};

export default SafeComponent(Gallery);
